import UIController, {uiSetTotalLoss} from "../UIController";
import {inject} from "inversify";
import SERVICE_IDENTIFIER from "../../constants/identifiers";
import SystemConfig, {CONFIG_PARAMS, MODES} from "../../system/SystemConfig";
import ButtonSounds from "../ButtonSounds";
import IAudio from "../../engine/IAudio";
import System from "../../system/System";
import MobileDOM, {ELEMENT_IDS} from "./MobileDOM";
import Wallet, {WALLET_EVENTS} from "../../wallet/Wallet";
import MobileUIActions from "./MobileUIActions";
import UISliderBets from "./sliders/UISliderBets";
import UISliderCoin from "./sliders/UISliderCoin";
import UISliderLines from "./sliders/UISliderLines";
import events from "../../events/EventsObservable";
import {ACTION_EVENTS} from "../UserAction";
import {STATES} from "../../states/states";
import {canToggleFsMode} from "../../wallet/fsModeToggle";
import {AUTOSTART_EVENTS, getNearestAutostart, MAX_AUTOSTART} from "../../game/GameAutostart";
import {AUDIO_EVENTS} from "../../engine/AbstractAudio";
import {EVENTS} from "../../events/baseEvents";
import AbstractUISlider from "./sliders/AbstractUISlider";
import {getNoUISliderRange as getRange, numberFormat} from "../../helpers";
import {BASE_ELEMENT_IDS} from "../BaseDOM";
import IGame from "../../game/IGame";
import IStatesManager from "../../states/IStatesManager";
import {GAME_EVENTS} from "../../game/gameEvents";
import {translate} from "../../translations/translations";
import AudioController from "../AudioController";
import { withCurrency } from "../../wallet/walletOperations";
import { MESSAGE_EVENTS } from "../../error/errorMessages";

export default class MobileUIController extends UIController {

    protected _gameset: any;
    protected _wallet: Wallet;
    protected _uiActions: MobileUIActions;

    dom: MobileDOM;

    public buttons: Array<HTMLElement>;

    protected _spinRotating: boolean;
    protected _burgerOpened: boolean = false;

    protected _betSlider: UISliderBets;
    protected _coinSlider: UISliderCoin;
    protected _linesSlider: UISliderLines;

    protected _spinBtnReset: boolean = false;

    constructor(
        @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
        @inject(SERVICE_IDENTIFIER.DOM) dom: MobileDOM,
        @inject(SERVICE_IDENTIFIER.StatesManager) statesManager: IStatesManager,
        @inject(SERVICE_IDENTIFIER.Game) game: IGame,
        @inject(SERVICE_IDENTIFIER.EngineAudio) audio: IAudio,
        @inject(SERVICE_IDENTIFIER.System) system: System,
        @inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
        @inject(SERVICE_IDENTIFIER.UIActions) uiActions: MobileUIActions
    ) {

        super(config, dom, statesManager, game, audio, system);

        this._gameset = config.gameset;
        this._wallet = wallet;
        this._uiActions = uiActions;

        this._spinRotating = false;
    }

    subscribe(): void {
        super.subscribe()

        events.subscribe(WALLET_EVENTS.WALLET_ON_INITED, (eventName, data) => this._walletInitEvent())
        events.subscribe(
            [
                WALLET_EVENTS.WALLET_ON_SET_BALANCE,
                WALLET_EVENTS.WALLET_ON_SET_FREESPINS
            ],
            (eventName, data) => this.walletSetBalance(data)
        )
        events.subscribe(
            [
                WALLET_EVENTS.WALLET_ON_SET_COIN,
                WALLET_EVENTS.WALLET_ON_SET_BET,
                WALLET_EVENTS.WALLET_ON_SET_LINES_COUNT
            ],
            (eventName, data) => this.walletOnSetBet(data)
        )
        events.subscribe(WALLET_EVENTS.WALLET_ON_TOTAL_LOSS, (eventName, data) => this._walletSetTotalLoss(data))
        events.subscribe(WALLET_EVENTS.WALLET_ON_SET_WIN, (eventName, data) => this.walletSetWin(data))
        events.subscribe(AUDIO_EVENTS.AUDIO_MUTED, (eventName, data) => this.audioMuted(data))


        events.subscribe(ACTION_EVENTS.ACTION_BURGER_OPEN, () => this._burgerOpen())
        events.subscribe([
                ACTION_EVENTS.ACTION_BURGER_CLOSE,
                ACTION_EVENTS.ACTION_PARAGRAPH_CLOSE,
                ACTION_EVENTS.ACTION_HISTORY_CLOSE
            ],
            () => this._burgerClose()
        )

        events.subscribe(ACTION_EVENTS.ACTION_BETMENU_OPEN, () => this._betMenuOpen())
        events.subscribe(ACTION_EVENTS.ACTION_BETMENU_CLOSE, () => this._betMenuClose())

        events.subscribe(EVENTS.INFO_ON_OPEN, () => this._infoOpen())
        events.subscribe(EVENTS.INFO_ON_CLOSE, () => this._infoClose())

        events.subscribe(ACTION_EVENTS.ACTION_AUTOSTART_OPEN, () => this._autostartMenuOpen())
        events.subscribe(ACTION_EVENTS.ACTION_AUTOSTART_CLOSE, () => this._autostartMenuClose())
        events.subscribe(ACTION_EVENTS.ACTION_AUTOSTART_CHOOSE, (eventName, data) => this.autostartChoose(data))
        events.subscribe(ACTION_EVENTS.ACTION_AUTOSTART_CONTINUE, () => this.autostartContinue())
        events.subscribe(AUTOSTART_EVENTS.AUTOSTART_ON_SET_COUNT, (eventName, data) => this._autostartUpdateEvent(data))

        events.subscribe(ACTION_EVENTS.ACTION_SETTINGS_OPEN, () => this.settingsMenuOpen())
        events.subscribe(ACTION_EVENTS.ACTION_SETTINGS_CLOSE, () => this.settingsMenuClose())
        events.subscribe(ACTION_EVENTS.ACTION_SETTINGS_SAVE, () => this.settingsSave())
        events.subscribe(ACTION_EVENTS.ACTION_SETTINGS_RESET, () => this.settingsReset())

        events.subscribe(ACTION_EVENTS.ACTION_HANDEDNESS_TOGGLE, () => this.toggleHandednes())
        events.subscribe(GAME_EVENTS.GAME_BIGWIN, () => this.onBigWin())
        events.subscribe(GAME_EVENTS.GAME_END_ROUND_RECALCED, () => this.onEndRound())
        events.subscribe(EVENTS.BEFORE_SPIN, () => this.beforeSpin())

        events.subscribe([MESSAGE_EVENTS.SWITCH_TO_FREESPINS, MESSAGE_EVENTS.SWITCH_TO_REAL], () => this.switchToFreespins())
        // IDLE only: the launch dialog can fire this during PREINFO / SOUND
        events.subscribe(GAME_EVENTS.GAME_FS_MODE_CHANGED, async () => {
            if (this.states.getState() === STATES.IDLE) await this.states.reset()
        })

        events.subscribe(ACTION_EVENTS.ACTION_SOUND_CHOOSE, (eventName, data) => {
            if (data !== "on") {
                this.audio.mute();
            } else {
                this.audio.unmute();
            }
            this.states.resolve(STATES.SOUND, data !== "on");
        });
    }

    switchToFreespins(): void {
        if(this._wallet.isBonus() && this.config.get(CONFIG_PARAMS.USE_FS_TOTAL_WIN)){
            const _creditBalance = numberFormat(this._wallet.moneyToCredit(this._wallet.getFSTotalWin()), this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), ".", " ");
            // const _creditBalance = numberFormat(this._wallet.getFSTotalWin(), this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), ".", " ");
            const _realBalanceCurrency=withCurrency(Number(this._wallet.getRealFsTotalWin()), this._wallet.getCurrency(),this._wallet.getDecimalPlaces())
            $("#" + ELEMENT_IDS.CREDIT).text(_creditBalance);
            $("#" + ELEMENT_IDS.BALANCE).text(_realBalanceCurrency);
        }
    }

    create() {
        this.buttons = [
            document.getElementById(ELEMENT_IDS.BUTTON_SPIN_WRAP),
            document.getElementById(ELEMENT_IDS.BUTTON_BET_OPEN),
            document.getElementById(ELEMENT_IDS.BUTTON_AUTOSTART_OPEN),
            document.getElementById(ELEMENT_IDS.BUTTON_BURGER),
            document.getElementById(ELEMENT_IDS.BUTTON_AUTOSTART_CONTINUE)
        ];
    }

    protected walletOnSetBet(data) {
        this.walletSetBet(data);
        this.walletSetBalance(data);
    }

    audioMuted(data): void {
        this._toggleSoundButton(data);
    }

    async actionSpin(data) {
        super.actionSpin(data);
        this._rotateSpinButton();
        await events.dispatch(EVENTS.INFO_CLOSE_REQUEST);
    }

    autostartChoose(data): void {
        this.game.autostart?.setAutostart(data);
        if(this.game.autostart?.isAutostart()) {
			events.dispatch(GAME_EVENTS.GAME_AUTOSTART_START)
            events.dispatch(EVENTS.SPIN);
        }
    }

    autostartContinue(): void {
        this.game.autostart?.continue();
        if(this.game.autostart?.isAutostart() && !this.game.round.isRunning()) {
			events.dispatch(GAME_EVENTS.GAME_AUTOSTART_START)
            events.dispatch(EVENTS.SPIN);
        }
    }

    onEndRound(): void {
        if (!this.game.isInFreeSpins()) {
            this.dom.winToBalance().then(() => {
                this._wallet.addWinToBalance();
                this._wallet.updateBalanceUI();
            });
        }
        if(this._wallet.isBonus() && this.config.get(CONFIG_PARAMS.USE_FS_TOTAL_WIN)){
            const _creditBalance = numberFormat(this._wallet.moneyToCredit(this._wallet.getFSTotalWin()), this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), ".", " ");
            // const _creditBalance = numberFormat(this._wallet.getFSTotalWin(), this._wallet.getCreditWinDecimalPlaces() || this._wallet.getDecimalPlaces(), ".", " ");
            const _realBalanceCurrency=withCurrency(Number(this._wallet.getRealFsTotalWin()), this._wallet.getCurrency(),this._wallet.getDecimalPlaces())
            $("#" + ELEMENT_IDS.CREDIT).text(_creditBalance);
            $("#" + ELEMENT_IDS.BALANCE).text(_realBalanceCurrency);
        }
    }

    async beforeSpin(): Promise<void>  {
        if (this._wallet.getWin() > 0) {
            await this.dom.winToBalance(false);
            await this.dom.fadeOutWin();
        }
    }

    visibleButtons(visible = true, except = []) {
        visibleElements(this.buttons, visible, except);
    }

    _walletInitEvent() {

        this._betSlider = new UISliderBets(this._wallet);
        this._coinSlider = new UISliderCoin(this._wallet);
        this._linesSlider = new UISliderLines(this._wallet);

        if(this._wallet.getLines().length < 2) {
            this._hideSlider(ELEMENT_IDS.SLIDER_LINES);
            $(`#${ELEMENT_IDS.LINES}Container`).hide();
            $(`#${ELEMENT_IDS.BET_LINE}Container`).hide();
        }

        if (this.config.get(CONFIG_PARAMS.ONLY_COINS) == 1) {
            this._hideSlider(ELEMENT_IDS.SLIDER_BET);
        }

        if(this._wallet.getCoinValues().length < 2) this._hideSlider(ELEMENT_IDS.SLIDER_COIN);


        $(".noUi-marker-normal").remove();

        AbstractUISlider.sliderPreviewBets(this._wallet);
    }

    private _hideSlider(id) {
        $(`#${id}Container`).hide();
    }

    private walletSetBet({creditBetLines, realBetCurrency, linesCount, creditBetAtomic}) {

        $("#" + ELEMENT_IDS.BET_CREDIT).text(" " + numberFormat(creditBetLines, 0, ".", " "));
        $("#" + ELEMENT_IDS.BET_REAL).text(" " + realBetCurrency);

        $("#" + ELEMENT_IDS.LINES).text(" " + linesCount);
        $("#" + ELEMENT_IDS.BET_LINE).text(" " + creditBetAtomic);

        this._recalcBetMenu();
    }

    private walletSetBalance({creditBalance, realBalanceCurrency, freespins}) {
        if (this.config.get(CONFIG_PARAMS.MODE) !== MODES.REPLAY) {
            $("#" + ELEMENT_IDS.BALANCE).text(" " + realBalanceCurrency);let _realBalanceCurrency = realBalanceCurrency
            let _creditBalance = numberFormat(creditBalance, 0, ".", " ");

			if(!(this._wallet.isBonus() && this.config.get(CONFIG_PARAMS.USE_FS_TOTAL_WIN))){
                $("#" + ELEMENT_IDS.CREDIT).text(_creditBalance);
                $("#" + ELEMENT_IDS.BALANCE).text(_realBalanceCurrency);
			}
            $("#" + ELEMENT_IDS.FREE_SPINS).text(" " + freespins);
            $($("#" + ELEMENT_IDS.BUTTON_FREESPINS).children('span')).text("" + freespins);
        } else {
            // Credit and Balance are set to * in replay mode
            $("#" + ELEMENT_IDS.CREDIT).text("****");
            $("#" + ELEMENT_IDS.BALANCE).text("****");
        }
    }

    private walletSetWin({win, realWinCurrency, atomicBet}) {
        let winElement = $("#" + ELEMENT_IDS.WIN_REAL);
        if (win > 0) {
            winElement.text(" " + realWinCurrency);
        } else {
            winElement.text(" ");
        }

        // if not in bigwin and win higher than bet, highlight win
        if (win > atomicBet) {
            this.dom.highlightWin();
        }
    }

    _autostartUpdateEvent({count}) {
        let buttons = $("#" + BASE_ELEMENT_IDS.BUTTON_STOP_AUTOSTART);
        buttons.text(count > MAX_AUTOSTART || count < 1 ? "" : count);
    }

    _toggleSoundButton(mute) {

        let button = document.getElementById(ELEMENT_IDS.BUTTON_BURGER_SOUND);

        if (!mute) {
            button.classList.remove("mx-ico-sound-off");
            button.classList.add("mx-ico-sound-on");
        } else {
            button.classList.remove("mx-ico-sound-on");
            button.classList.add("mx-ico-sound-off");
        }
    }

    _rotateSpinButton() {

        if(this.game.autostart?.isAutostart() || this._spinRotating) {
            return;
        }

        let buttonWrap = document.getElementById(ELEMENT_IDS.BUTTON_SPIN_WRAP);

        $(buttonWrap).show();

        this._spinRotating = true;

        buttonWrap.classList.add("button-rotating");
        setTimeout(() => {
            this._spinRotating = false;
            buttonWrap.classList.remove("button-rotating");
        }, 300);
    }

    _burgerOpen() {

        if(this._burgerOpened) return;
        this._burgerOpened = true;

        return new Promise<void>(res => {

            let burgerMenu = document.getElementById(ELEMENT_IDS.MENU_BURGER);
            let burgerButton = $("#" + ELEMENT_IDS.BUTTON_BURGER);

            burgerButton.css("filter",  "invert(1)");

            this.dom.bg.showBg();

            visibleElements(this.buttons, false);
            this.visibleFSButton(false);

            burgerButton.show();
            $(burgerMenu).show();
            burgerMenu.style.opacity = "1";

            burgerButton.data("action", ACTION_EVENTS.ACTION_BURGER_CLOSE);

            setTimeout(() => {
                document.querySelectorAll(".burger-button").forEach((btn) => {
                    btn.classList.add("mx-anim-burger-scalein");
                });

                res();
            });

        });
    }

    async _burgerClose(delay: number = 210, hideBg: boolean = true) {

        if(!this._burgerOpened) return;

        return new Promise<void>(res => {

            let burgerMenu = document.getElementById(ELEMENT_IDS.MENU_BURGER);
            let burgerButton = $("#" + ELEMENT_IDS.BUTTON_BURGER);

            setTimeout(() => {

                hideBg && this.dom.bg.hideBg();
                $(burgerMenu).hide();
                burgerMenu.style.opacity = "0";

                document.querySelectorAll(".burger-button").forEach((btn) => {
                    btn.classList.remove("mx-anim-burger-scalein");
                });

                burgerButton.data("action", ACTION_EVENTS.ACTION_BURGER_OPEN);
                burgerButton.css("filter",  "");

                setTimeout(() => {

                    res();

                    this.states.reset();

                    this._burgerOpened = false;

                }, delay);
            });
        });
    }

    _betMenuOpen() {

        this.dom.bg.showBg();

        this._recalcBetMenu();

        visibleElements(this.buttons, false);
        this.visibleFSButton(false);

        $("#" + ELEMENT_IDS.MENU_BET).show();
        $("#" + ELEMENT_IDS.MENU_BET).css("opacity", 1);
    }

    _recalcBetMenu() {
        if (!this._wallet.isBonus()) {
            this._betSlider.setValue(this._wallet.getCreditBet());
            this._coinSlider.setValue(this._wallet.getCoinValue());
            this._linesSlider.setValue(this._wallet.getLinesCount());

            AbstractUISlider.sliderPreviewBets(this._wallet);
        }
    }

    _betMenuClose() {

        this.dom.bg.hideBg();

        let betSlider = document.getElementById(ELEMENT_IDS.SLIDER_BET);
        let linesSlider = document.getElementById(ELEMENT_IDS.SLIDER_LINES);
        let coinSlider = document.getElementById(ELEMENT_IDS.SLIDER_COIN);


        // @ts-ignore
        let bet = betSlider.noUiSlider.get() * 1;
        // @ts-ignore
        let lines = linesSlider.noUiSlider.get() * 1;
        // @ts-ignore
        let coinValue = coinSlider.noUiSlider.get() * 1;

        this._wallet.updateWallet(bet, coinValue, lines);

        $("#" + ELEMENT_IDS.MENU_BET).css("opacity", 0);
        $("#" + ELEMENT_IDS.MENU_BET).hide();

        this.states.reset();


        if (this._wallet.getWin()) {
            this.dom.fadeOutWin().then(() => {
                this._wallet.setWin(0);
            });
        }
    }

    _autostartMenuOpen() {
        if (!(this.states.getState() === STATES.IDLE || this.states.getState() === STATES.FS_IDLE)) {
            return
        }

        this.dom.bg.showBg();

        let count = this.game.autostart?.getAutostart();

        let autostartSlider = document.getElementById(ELEMENT_IDS.SLIDER_AUTOSTART);
        // @ts-ignore
        autostartSlider.noUiSlider.set(getNearestAutostart(count, this.game.autostart?.getValues()));

        visibleElements(this.buttons, false);
        this.visibleFSButton(false);

        let range = this.dom.getDefaultRange();
        let text = translate("off", this.config.get(CONFIG_PARAMS.LANGUAGE));
        if (this._wallet.isBonus()) {
            let max = this._wallet.getFreeSpins();
            range = this.game.autostart?.getValues();
            range = range.filter(function (x) {
                return x <= max;
            });
            // if range contains Infinity, replace it for 1000
            range.indexOf(Infinity) !== -1 && range.splice(range.indexOf(Infinity), 1, 1000);
            if (range.length <= 2) {
                range.push(this._wallet.getFreeSpins())
            }
        }
        this.dom.autostartSlider.updateOptions({
            start: null,
            range: getRange(range),
            pips: {
                mode: "steps",
                format: {
                    to: v => v == 0 ? text  : (v == 1000) ? "" : v,
                    from: v => parseInt(v)
                }
            }
        }, false);

        let element = $('#'+ELEMENT_IDS.SLIDER_AUTOSTART)[0];
        element.querySelectorAll('.noUi-value').forEach((el) => {
            el.addEventListener("click", (e) => {
                // @ts-ignore
                element.noUiSlider.set(parseInt(e.target.getAttribute("data-value"), 10));
                this.dom.sliderUpdate();
            });
        });

        $("#" + ELEMENT_IDS.MENU_AUTOSTART).show();
        $("#" + ELEMENT_IDS.MENU_AUTOSTART).css("opacity", 1);
    }

    _autostartMenuClose() {

        this.dom.bg.hideBg();

        let autostartSlider = document.getElementById(ELEMENT_IDS.SLIDER_AUTOSTART);
        // @ts-ignore
        let autostart = autostartSlider.noUiSlider.get() * 1;

        this.game.autostart?.setAutostart(autostart > MAX_AUTOSTART ? Infinity : autostart);

        $("#" + ELEMENT_IDS.MENU_AUTOSTART).hide();
        $("#" + ELEMENT_IDS.MENU_AUTOSTART).css("opacity", 0);

        this.states.reset();

        if(this.game.autostart?.isAutostart()) {
			events.dispatch(GAME_EVENTS.GAME_AUTOSTART_START)
            events.dispatch(EVENTS.SPIN);
        }
    }

    _infoOpen() {

        visibleElements(this.buttons, false);
        this.visibleFSButton(false);
        $("#" + ELEMENT_IDS.MENU_BURGER).hide();
        $("#" + ELEMENT_IDS.MENU_BURGER).css("opacity", 0);

        $(".burger-button").removeClass("mx-anim-burger-scalein");

        this.dom.bg.hideBg();

        visibleInfopages(true);
    }

    async _infoClose() {

        visibleInfopages(false);

        await this._burgerClose(0);
        await this.states.reset();
    }

    // _infoBoxBalanceShow() {
    //     let box = document.getElementById(BASE_ELEMENT_IDS.INFO_BOX_BALANCE);
    //
    // }

    _walletSetTotalLoss({loss, lossWithCurrency}) {
        if(super._isValidClientPlatform() && this.config.get(CONFIG_PARAMS.LEGISLATION) === "cz") {
            uiSetTotalLoss({loss, lossWithCurrency});
        }
    }

    toggleHandednes() {
        if (this.dom.leftHanded) {
            this.setRightHandedUI();
        } else {
            this.setLeftHandedUI();
        }
    }

    /**
     * Set UI to left handed settings. Replace wrapper class right-handed for left-handed
     * to change CSS styles. Data is set
     */
    setLeftHandedUI() {
        let wrapper = $("#" + BASE_ELEMENT_IDS.WRAPPER);
        wrapper.removeClass("right-handed");
        wrapper.addClass("left-handed");
        $("#" + ELEMENT_IDS.BUTTON_BURGER_HANDEDNESS).data("data", "right");
        this.dom.leftHanded = true;
    }

    setRightHandedUI() {
        let wrapper = $("#" + BASE_ELEMENT_IDS.WRAPPER);
        wrapper.removeClass("left-handed");
        wrapper.addClass("right-handed");
        $("#" + ELEMENT_IDS.BUTTON_BURGER_HANDEDNESS).data("data", "left");
        this.dom.leftHanded = false;
    }

    getHand() {
        return this.dom.leftHanded ? "left" : "right";
    }

    onBigWin() {
        if (window.navigator && window.navigator.vibrate) {
            window.navigator.vibrate(1000);
        }
    }

    visibleFSButton(visible: boolean = true) {
        if (this._wallet.getFreeSpins() > 0) {
            let fsBtn = $(`#${ELEMENT_IDS.BUTTON_FREESPINS}`);
            visible && this._wallet.isValidBonusBet() ? fsBtn.show() : fsBtn.hide();
        }
    }

    /** States (IDLE) only: label, visibility and enabled state of the real <-> free spins toggle. */
    updateFsButton() {
        this.dom.refreshFsButtonLabel();
        const fsBtn = $(`#${ELEMENT_IDS.BUTTON_FREESPINS}`);
        this._wallet.getFreeSpins() > 0 && this._wallet.isValidBonusBet() ? fsBtn.show() : fsBtn.hide();
        fsBtn.prop(
            "disabled",
            !canToggleFsMode({
                state: STATES.IDLE,
                roundRunning: this.game.round.isRunning(),
                roundUnfinished: this.game.round.isUnfinished(),
                inGameFreespins: this.game.isInFreeSpins(),
                autostart: this.game.autostart.isAutostart(),
                freeSpins: this._wallet.getFreeSpins(),
                validBonusBet: this._wallet.isValidBonusBet(),
            })
        );
    }


    public async settingsMenuOpen() {
        if (!(this.dom.isSettingsMenuAllowed() && this._uiActions.isActionAllowed(ACTION_EVENTS.ACTION_SETTINGS_OPEN))) {
            return
        }

        await this._burgerClose(0, false);

        this.dom.bg.showBg();

        if (this.dom.settingsMenu) {
            this.dom.settingsMenu.open();
        }

        visibleElements(this.buttons, false);
        this.visibleFSButton(false);
    }

    public settingsMenuClose() {
        if (!(this.dom.isSettingsMenuAllowed() && this._uiActions.isActionAllowed(ACTION_EVENTS.ACTION_SETTINGS_CLOSE))) {
            return
        }
        this.dom.bg.hideBg();
        if (this.dom.settingsMenu) {
            this.dom.settingsMenu.close();
        }

        visibleElements(this.buttons, true);
        this.visibleFSButton(true);
        this.states.reset();
    }

    public settingsSave(top: string = null, right: string = null) {
       if (!(this.dom.isSettingsMenuAllowed() && this._uiActions.isActionAllowed(ACTION_EVENTS.ACTION_SETTINGS_SAVE))) {
            return
        }
        if (this.dom.settingsMenu) {
            this.dom.settingsMenu.save(top, right);
        }
        this.settingsMenuClose();
    }

    public settingsSetPosition(top: number|null = null, right: number|null = null) {
        if (this.dom.settingsMenu) {
            this.dom.settingsMenu.setPosition(top, right);
            this.dom.settingsMenu.updatePosition();
        }
    }

    public settingsReset() {
        if (!(this.dom.isSettingsMenuAllowed() && this._uiActions.isActionAllowed(ACTION_EVENTS.ACTION_SETTINGS_RESET))) {
            return
        }
        if (this.dom.settingsMenu) {
            this.dom.settingsMenu.reset();
        }
    }
}

/**
 * @param elements
 * @param visible
 * @param except
 * @return {Array}
 */
export function visibleElements(elements, visible = true, except = []) {

    let changed = [];

    if(typeof except === "string") {
        except = [except];
    }

    except = except.map(e => typeof e === "string" ? document.getElementById(e) : e);

    let elms = elements.filter(e => !except.includes(e)); //.map(e => e); //.join(",");

    // console.log(elements, elms);

    $(elms).each(function() {

        let el = $(this);

        if(visible) {
            if(!el.is(':visible')) {
                el.show();
                changed.push(this);
            }
        } else {
            if(el.is(':visible')) {
                el.hide();
                changed.push(this);
            }
        }
    });

    return changed;
}

export function visibleInfopages(visible = true) {
    visible ? $(`#${BASE_ELEMENT_IDS.WINTABLE_BUTTONS_CONTAINER}`).show() : $(`#${BASE_ELEMENT_IDS.WINTABLE_BUTTONS_CONTAINER}`).hide()
}